// The site's one init. Everything on the page works without it; it adds the theme switch, the hero's rotation and the headline word.
// Small single-purpose classes, dependencies passed in, one composition at the bottom.

type ThemeChoice = "light" | "dark" | "system";

const THEME_KEY = "theme";

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** The theme switch: light, dark or the system's setting. The choice is the only thing the site ever stores in the browser. */
class ThemeSwitch {
  constructor(private readonly root: HTMLElement, private readonly buttons: HTMLButtonElement[], private readonly storage: Storage | null) {}

  start(): void {
    this.show(this.current());
    for (const button of this.buttons) {
      button.addEventListener("click", () => this.choose(button.dataset["themeSet"] as ThemeChoice));
    }
  }

  private current(): ThemeChoice {
    const attribute = this.root.getAttribute("data-theme");
    return attribute === "light" || attribute === "dark" ? attribute : "system";
  }

  private choose(choice: ThemeChoice): void {
    if (choice === "system") {
      this.root.removeAttribute("data-theme");
      this.write(null);
    } else {
      this.root.setAttribute("data-theme", choice);
      this.write(choice);
    }
    this.show(choice);
  }

  private write(value: string | null): void {
    try {
      if (value === null) {
        this.storage?.removeItem(THEME_KEY);
      } else {
        this.storage?.setItem(THEME_KEY, value);
      }
    } catch {
      // Not stored: the choice holds for this page only.
    }
  }

  private show(choice: ThemeChoice): void {
    for (const button of this.buttons) {
      button.setAttribute("aria-pressed", String(button.dataset["themeSet"] === choice));
    }
  }
}

/** Swaps the headline word with a short glitch: random letters that settle on the new word. */
class ScrambleWord {
  private static readonly glyphs = "abcdefghijklmnopqrstuvwxyz#%&*+=<>/";
  private static readonly duration = 650;
  private frame = 0;

  constructor(private readonly visible: HTMLElement, private readonly spoken: HTMLElement | null, private readonly reduceMotion: boolean) {}

  to(word: string): void {
    cancelAnimationFrame(this.frame);
    if (this.spoken) {
      this.spoken.textContent = word;
    }
    if (this.reduceMotion) {
      this.visible.textContent = word;
      return;
    }
    const started = performance.now();
    const tick = (now: number): void => {
      const progress = Math.min(1, (now - started) / ScrambleWord.duration);
      this.render(word, progress);
      if (progress < 1) {
        this.frame = requestAnimationFrame(tick);
      }
    };
    this.frame = requestAnimationFrame(tick);
  }

  private render(word: string, progress: number): void {
    const letters = [...word];
    const parts = letters.map((letter, index) => {
      const settled = progress > 0.35 + (index / letters.length) * 0.6;
      const span = document.createElement("i");
      if (settled || letter === " ") {
        span.textContent = letter === " " ? "\u00a0" : letter;
        return span;
      }
      span.textContent = ScrambleWord.glyphs[Math.floor(Math.random() * ScrambleWord.glyphs.length)] ?? letter;
      span.className = Math.random() < 0.5 ? "t1" : "t2";
      span.style.transform = `translateY(${Math.round((Math.random() - 0.5) * 14)}px)`;
      return span;
    });
    this.visible.replaceChildren(...parts);
  }
}

/** The hero's four windows: one open at a time, rotating by itself unless paused, hovered or the visitor asks for less motion. */
class HeroRotation {
  private active = 0;
  private paused = false;
  private hovering = false;
  private timer = 0;

  constructor(
    private readonly panels: HTMLElement[],
    private readonly progress: HTMLElement | null,
    private readonly word: ScrambleWord | null,
    private readonly words: string[],
    private readonly duration: number,
    private readonly reduceMotion: boolean,
  ) {}

  start(visual: HTMLElement, pauseButton: HTMLButtonElement | null): void {
    this.panels.forEach((panel, index) => {
      panel.querySelector<HTMLButtonElement>(".hv-tab")?.addEventListener("click", () => this.select(index));
      panel.addEventListener("mouseenter", () => {
        if (index !== this.active && window.matchMedia("(hover: hover) and (min-width: 1024px)").matches) {
          this.select(index);
        }
      });
    });
    visual.addEventListener("mouseenter", () => this.setHover(true));
    visual.addEventListener("mouseleave", () => this.setHover(false));
    pauseButton?.addEventListener("click", () => this.togglePause(pauseButton));
    this.schedule();
  }

  private select(index: number): void {
    this.active = index;
    this.panels.forEach((panel, i) => {
      panel.dataset["active"] = String(i === index);
      panel.querySelector(".hv-tab")?.setAttribute("aria-expanded", String(i === index));
    });
    const next = this.words[index];
    if (next) {
      this.word?.to(next);
    }
    this.schedule();
  }

  private setHover(value: boolean): void {
    this.hovering = value;
    this.schedule();
  }

  private togglePause(button: HTMLButtonElement): void {
    this.paused = !this.paused;
    const label = this.paused ? button.dataset["labelPlay"] : button.dataset["labelPause"];
    if (label) {
      button.setAttribute("aria-label", label);
    }
    this.schedule();
  }

  private halted(): boolean {
    return this.paused || this.hovering || this.reduceMotion;
  }

  private schedule(): void {
    window.clearTimeout(this.timer);
    this.restartProgress();
    if (this.halted()) {
      return;
    }
    this.timer = window.setTimeout(() => this.select((this.active + 1) % this.panels.length), this.duration);
  }

  private restartProgress(): void {
    const bar = this.progress;
    if (!bar) {
      return;
    }
    bar.classList.remove("run");
    bar.classList.toggle("paused", this.halted());
    void bar.offsetWidth; // restart the CSS animation
    bar.style.setProperty("--hero-ms", `${this.duration}ms`);
    if (!this.reduceMotion) {
      bar.classList.add("run");
    }
  }
}

function startHero(reduceMotion: boolean): void {
  const hero = document.querySelector<HTMLElement>("[data-hero]");
  const visual = hero?.querySelector<HTMLElement>("[data-hero-visual]");
  if (!hero || !visual) {
    return;
  }
  const panels = [...visual.querySelectorAll<HTMLElement>(".hv-panel")];
  const scrambleHost = hero.querySelector<HTMLElement>("[data-scramble]");
  const words = (scrambleHost?.dataset["words"] ?? "").split("|").filter((w) => w.length > 0);
  const word = scrambleHost
    ? new ScrambleWord(scrambleHost.querySelector<HTMLElement>("[data-scramble-text]") ?? scrambleHost, scrambleHost.querySelector<HTMLElement>(".sr-only"), reduceMotion)
    : null;
  const duration = Number(hero.dataset["duration"] ?? "4500");
  new HeroRotation(panels, hero.querySelector<HTMLElement>(".hero-progress"), word, words, duration, reduceMotion).start(
    visual,
    hero.querySelector<HTMLButtonElement>("[data-hero-pause]"),
  );
}

function init(): void {
  const reduceMotion = prefersReducedMotion();
  let storage: Storage | null = null;
  try {
    storage = window.localStorage;
  } catch {
    storage = null;
  }
  const buttons = [...document.querySelectorAll<HTMLButtonElement>("[data-theme-set]")];
  new ThemeSwitch(document.documentElement, buttons, storage).start();
  startHero(reduceMotion);
}

init();

